import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import type { BeforeMount } from "@monaco-editor/react";
import { BottomSheet, DsButton, ModalDialog, SheetRows } from "../ds";
import type { SheetRow } from "../ds";
import {
  AlertIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  FileIcon,
  FolderDownIcon,
  FolderIcon,
  MoreHorizontalIcon,
  PlusIcon,
  SearchIcon,
  UploadIcon,
} from "../ds/icons";
import { TOAST_SURFACE } from "../ds/toast";
import {
  ApiV2Error,
  deployUpload,
  editSource,
  getAppSource,
  listSourceFolder,
  searchSourceFiles,
  sourceFileContent,
  SourceFileContent,
  SourceFolder,
  SourceSearch,
} from "../../utils/apiV2";
import { userFacingError } from "../../utils/apiV2";
import {
  Conflict,
  createFile,
  deleteFile,
  dirtyPaths,
  discardFile,
  editsBody,
  EMPTY_WORKSPACE,
  FileStatus,
  hasUnsavedUnder,
  isDirty,
  keepMine,
  OverlaidEntry,
  overlayFolder,
  pathProblem,
  rebase,
  renameFile,
  setContent,
  sourcePathOf,
  SourceWorkspace,
  statusOf,
  takeTheirs,
  withOriginal,
} from "../../utils/sourceEdits";

/**
 * The app's uploaded code — the tree on the left, Monaco on the right, on
 * its own dark grey as the Agent's code panel is. Fills whatever box it is
 * given: the Source Code screen gives it the rest of the window.
 *
 * With `canEdit`, the latest code can be edited in place: files changed,
 * made, renamed and deleted, all kept in a workspace (utils/sourceEdits)
 * until "Save & deploy" turns them into one new upload and deploys it. The
 * workspace lives apart from the tree and the files, which reload when the
 * code changes; the guards (leaving the page, closing the tab) ask first
 * while it holds anything.
 *
 * File contents come from the API, one file at a time, never from a storage
 * link: access is checked per file, and binary or large files come back as a
 * notice instead of bytes.
 *
 * The tree comes the same way, one folder at a time as it is opened, and
 * search runs on the API. It used to fetch every path of the code up front and
 * build the whole tree in the browser, which froze the page on a project of
 * tens of thousands of files.
 */

// Only loaded when someone opens the viewer.
const Editor = lazy(() => import("@monaco-editor/react"));

// The editor's own dark theme and the Courrier face, as the Agent's code
// panel has them: code on a neutral dark grey reads as code, where the navy
// of the rest of the screen made it read as one more panel of the page.
const EDITOR_BACKGROUND = "#1E1E1E";
const EDITOR_FONT = '"Courrier", "Courier Prime", ui-monospace, monospace';

// The JavaScript and TypeScript checkers have nothing to say that helps:
// their errors and hints ("convert to import", dotted under require()) are
// noise over code someone uploaded to run, and the build is the judge of it.
type LanguageDefaults = {
  setDiagnosticsOptions: (options: Record<string, boolean>) => void;
};
const quietDiagnostics: BeforeMount = (monaco) => {
  // The CDN build the loader fetches fills `languages.typescript`; 0.55's ESM
  // build moved it to a top-level `typescript` and left a stub (the Agent's
  // code panel reads it the same way).
  const typescript = ((monaco as unknown as { typescript?: unknown })
    .typescript ?? monaco.languages.typescript) as {
    typescriptDefaults?: LanguageDefaults;
    javascriptDefaults?: LanguageDefaults;
  };
  const off = {
    noSemanticValidation: true,
    noSyntaxValidation: true,
    noSuggestionDiagnostics: true,
  };
  typescript.typescriptDefaults?.setDiagnosticsOptions(off);
  typescript.javascriptDefaults?.setDiagnosticsOptions(off);
};

interface SourceFile {
  path: string;
  size: number;
}

type Loadable<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | ({ status: "ready" } & T);

// Typing pauses this long before the API is asked.
const SEARCH_DEBOUNCE_MS = 250;

const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiV2Error ? e.message : fallback;

const LANGUAGES: Record<string, string> = {
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "javascript",
  ts: "typescript",
  tsx: "typescript",
  mts: "typescript",
  json: "json",
  html: "html",
  htm: "html",
  css: "css",
  scss: "scss",
  less: "less",
  md: "markdown",
  markdown: "markdown",
  xml: "xml",
  svg: "xml",
  yaml: "yaml",
  yml: "yaml",
  toml: "ini",
  ini: "ini",
  env: "ini",
  sh: "shell",
  bash: "shell",
  sql: "sql",
  graphql: "graphql",
  py: "python",
  go: "go",
  rb: "ruby",
  java: "java",
  kt: "kotlin",
  php: "php",
  rs: "rust",
  cs: "csharp",
  swift: "swift",
  scala: "scala",
  ex: "elixir",
  exs: "elixir",
};

const fileName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

const extensionOf = (path: string) => {
  const name = fileName(path);
  return name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
};

const isDockerfile = (path: string) => {
  const name = fileName(path);
  return name === "Dockerfile" || name.endsWith(".dockerfile");
};

const languageFor = (path: string) => {
  if (isDockerfile(path)) return "dockerfile";
  if (fileName(path) === "Makefile") return "makefile";
  return LANGUAGES[extensionOf(path)] || "plaintext";
};

// Where the viewer opens: the file at the top that says what the app is.
const INITIAL_FILES = [
  "Dockerfile",
  "package.json",
  "requirements.txt",
  "go.mod",
  "README.md",
];

const dirOf = (path: string) =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";

// A line in the tree that is not a row: loading, an error, "and more".
const TreeHint = ({
  depth,
  isError = false,
  children,
}: {
  depth: number;
  isError?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={`py-1.5 pr-2 font-inter text-ds-125 ${
      isError ? "text-state-err" : "text-navy-300"
    }`}
    style={{ paddingLeft: 8 + depth * 18 }}
  >
    {children}
  </div>
);

// What the tree says beside a path that differs from the uploaded code: a
// dot after the name, as the Agents code panel marks an unsaved file. The
// colour says which way it differs; the tooltip says it in words.
const STATUS_DOT: Record<
  Exclude<FileStatus, null>,
  { title: string; className: string }
> = {
  modified: { title: "Changed, not saved", className: "bg-state-warn" },
  new: { title: "New, not saved", className: "bg-state-ok-soft" },
  renamed: { title: "Renamed, not saved", className: "bg-action-soft" },
  deleted: { title: "Deleted in the next save", className: "bg-state-err" },
};

const StatusMark = ({ status }: { status: FileStatus }) =>
  status ? (
    <span
      role="img"
      aria-label={STATUS_DOT[status].title}
      title={STATUS_DOT[status].title}
      className={`flex-none w-1.5 h-1.5 rounded-full ${STATUS_DOT[status].className}`}
    />
  ) : null;

/** The open file's edits behind one "⋯" on the file bar: a row of them
 * beside copy and download read as a toolbar to learn. */
const BarMenu = ({
  label,
  items,
}: {
  label: string;
  items: { label: string; onClick: () => void; isDanger?: boolean }[];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setIsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  return (
    <span ref={ref} className="relative flex-none">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={`${BAR_BUTTON} w-7`}
      >
        <MoreHorizontalIcon size={14} />
      </button>
      {isOpen ? (
        <ul
          role="menu"
          className="absolute right-0 top-full z-20 mt-1.5 m-0 p-1 list-none min-w-[11rem] rounded-card border border-navy-500 bg-navy-800 shadow-[0_12px_32px_rgba(0,0,0,0.45)]"
        >
          {items.map((item) => (
            <li key={item.label}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
                className={`b4-focus w-full rounded-control px-3 py-1.5 text-left font-inter text-ds-125 hover:bg-navy-600 ${
                  item.isDanger
                    ? "text-state-err"
                    : "text-navy-100 hover:text-white"
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
};

const EMPTY_FOLDER: Loadable<SourceFolder> = {
  status: "ready",
  dir: "",
  fileCount: 0,
  total: 0,
  truncated: false,
  entries: [],
};

/** What the tree may do: given by the editor, absent in the read-only viewer. */
interface TreeActions {
  newFile: (dir: string) => void;
  rename: (path: string) => void;
  remove: (path: string) => void;
  discard: (path: string) => void;
}

const rowClass = (isSelected: boolean, isFile: boolean, status: FileStatus) =>
  `b4-focus w-full flex items-center gap-2 pr-1 h-8 rounded-control text-left font-inter text-ds-135 ${
    isSelected
      ? "bg-navy-600 text-white"
      : isFile
      ? "text-navy-100 hover:bg-navy-700"
      : "text-navy-200 hover:bg-navy-700"
  } ${status === "deleted" ? "line-through opacity-60" : ""}`;

// Folders carry a disclosure triangle and a trailing slash; a file sits
// where a folder's name starts, so each level reads as one column. A folder's
// rows are drawn from what the API answered for it, with the editor's
// unsaved work drawn over, and only once it is open.
const FolderRows = ({
  dir,
  depth,
  folders,
  workspace,
  open,
  selected,
  onToggle,
  onSelect,
  actions,
}: {
  dir: string;
  depth: number;
  folders: Record<string, Loadable<SourceFolder>>;
  workspace: SourceWorkspace;
  open: Set<string>;
  selected: string | null;
  onToggle: (entry: OverlaidEntry) => void;
  onSelect: (file: SourceFile) => void;
  actions?: TreeActions;
}) => {
  // A folder that exists only through unsaved files was never asked for:
  // its listing is empty, and the work drawn over it is all it holds.
  const listed = folders[dir];
  const folder =
    dir &&
    hasUnsavedUnder(workspace, dir) &&
    (!listed || listed.status === "error")
      ? EMPTY_FOLDER
      : listed;
  if (!folder || folder.status === "loading") {
    return <TreeHint depth={depth}>Loading…</TreeHint>;
  }
  if (folder.status === "error") {
    return (
      <TreeHint depth={depth} isError>
        {folder.message}
      </TreeHint>
    );
  }
  const entries = overlayFolder(workspace, dir, folder.entries);
  // The disclosure column only where this level has a folder to open: a
  // plain list of files starts at the icon.
  const hasFolders = entries.some((entry) => entry.type === "FOLDER");
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
      {entries.map((entry) => {
        const isFile = entry.type === "FILE";
        const isOpen = !isFile && open.has(entry.path);
        const isSelected = entry.path === selected;
        return (
          <li key={`${isFile ? "f" : "d"}:${entry.path}`}>
            <button
              type="button"
              aria-expanded={isFile ? undefined : isOpen}
              onClick={() =>
                entry.type === "FILE"
                  ? onSelect({ path: entry.path, size: entry.size })
                  : onToggle(entry)
              }
              title={
                entry.type === "FOLDER"
                  ? entry.isVirtual
                    ? `${entry.path}/ · unsaved`
                    : `${entry.path}/ · ${entry.fileCount.toLocaleString()} ${
                        entry.fileCount === 1 ? "file" : "files"
                      }`
                  : entry.path
              }
              className={rowClass(isSelected, isFile, entry.status)}
              style={{ paddingLeft: 8 + depth * 18 }}
            >
              {hasFolders ? (
                <span
                  className="w-3 flex-none text-[10px] text-navy-300"
                  aria-hidden
                >
                  {isFile ? "" : isOpen ? "▾" : "▸"}
                </span>
              ) : null}
              <span
                className={`flex-none ${
                  isSelected ? "text-white" : "text-navy-300"
                }`}
              >
                {isFile ? <FileIcon size={14} /> : <FolderIcon size={14} />}
              </span>
              <span className="truncate">
                {isFile ? entry.name : `${entry.name}/`}
              </span>
              <StatusMark status={entry.status} />
            </button>
            {isOpen ? (
              <FolderRows
                dir={entry.path}
                depth={depth + 1}
                folders={folders}
                workspace={workspace}
                open={open}
                selected={selected}
                onToggle={onToggle}
                onSelect={onSelect}
                actions={actions}
              />
            ) : null}
          </li>
        );
      })}
      {folder.truncated ? (
        <li>
          <TreeHint depth={depth}>
            {`${folder.entries.length.toLocaleString()} of ${folder.total.toLocaleString()} shown. Search to find the rest.`}
          </TreeHint>
        </li>
      ) : null}
    </ul>
  );
};

// While searching: the matching files as one flat list, the folder dimmed
// after the name. Unsaved files are not in it; a moved-away one is left out.
const SearchRows = ({
  search,
  workspace,
  selected,
  onSelect,
}: {
  search: Loadable<SourceSearch>;
  workspace: SourceWorkspace;
  selected: string | null;
  onSelect: (file: SourceFile) => void;
}) => {
  if (search.status === "loading") {
    return <TreeHint depth={0}>Searching…</TreeHint>;
  }
  if (search.status === "error") {
    return (
      <TreeHint depth={0} isError>
        {search.message}
      </TreeHint>
    );
  }
  const files = search.files.filter((file) => {
    const removed = workspace.removed[file.path];
    return !removed || removed === "deleted";
  });
  if (!files.length) {
    return <TreeHint depth={0}>No file matches.</TreeHint>;
  }
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
      {files.map((file) => {
        const isSelected = file.path === selected;
        const dir = dirOf(file.path);
        const status = statusOf(workspace, file.path);
        return (
          <li key={file.path}>
            <button
              type="button"
              onClick={() => onSelect(file)}
              title={file.path}
              className={rowClass(isSelected, true, status)}
              style={{ paddingLeft: 8 }}
            >
              <span
                className={`flex-none ${
                  isSelected ? "text-white" : "text-navy-300"
                }`}
              >
                <FileIcon size={14} />
              </span>
              <span className="flex-none max-w-[60%] truncate">
                {file.path.slice(dir ? dir.length + 1 : 0)}
              </span>
              {dir ? (
                <span className="min-w-0 truncate text-ds-105 text-navy-300">
                  {dir}/
                </span>
              ) : null}
              <StatusMark status={status} />
            </button>
          </li>
        );
      })}
      {search.truncated ? (
        <li>
          <TreeHint depth={0}>
            {`First ${search.files.length.toLocaleString()} of ${search.total.toLocaleString()} matches. Type more to narrow it.`}
          </TreeHint>
        </li>
      ) : null}
    </ul>
  );
};

const Notice = ({ children }: { children: React.ReactNode }) => (
  <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center text-ds-125 text-navy-200">
    {children}
  </div>
);

// The file bar's buttons, as the Agents code panel draws its own: 28px,
// an icon and no label for the per-file actions, a short label only for the
// whole project's export. What they do is in the tooltip.
const BAR_BUTTON =
  "b4-focus inline-flex flex-none items-center justify-center h-7 rounded-control border border-navy-400 bg-navy-600 text-white transition-[filter] duration-[120ms] enabled:hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed";
const BAR_LABEL_BUTTON = `${BAR_BUTTON} gap-1.5 px-3 font-inter text-ds-115 font-medium whitespace-nowrap`;
// The bar's one blue button: the action of the moment (Upload, or Save & deploy).
const BAR_ACTION_BUTTON =
  "b4-focus inline-flex flex-none items-center justify-center gap-1.5 h-7 px-3 rounded-control border border-transparent bg-action text-white font-inter text-ds-115 font-medium whitespace-nowrap shadow-[inset_0_1px_0_rgba(255,255,255,.15)] transition-[filter] duration-[120ms] enabled:hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed";

const BarIconButton = ({
  label,
  onClick,
  isDisabled,
  children,
}: {
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={isDisabled}
    onClick={onClick}
    className={`${BAR_BUTTON} w-7`}
  >
    {children}
  </button>
);

const ExportButton = ({
  onClick,
  isBusy,
}: {
  onClick: () => void;
  isBusy: boolean;
}) => (
  <button
    type="button"
    title="Export the whole code as a .zip"
    disabled={isBusy}
    onClick={onClick}
    className={BAR_LABEL_BUTTON}
  >
    <FolderDownIcon size={13} />
    {isBusy ? "Preparing…" : "Export"}
  </button>
);

// The screen's own action, on the bar with the rest so the bar reads as one
// row of what you can do with the code — it stood alone on the head, a
// full-size blue button over a strip of small grey ones.
const UploadButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    title="Upload a folder and deploy it"
    onClick={onClick}
    className={BAR_ACTION_BUTTON}
  >
    <UploadIcon size={13} />
    Upload
  </button>
);

/** Saves a text file through the browser, named as the file is. */
const saveText = (path: string, text: string) => {
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/plain;charset=utf-8" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = path.split("/").pop() || "file.txt";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const COPIED_MS = 1800;

/** "New file" and "Rename": one path, checked as it is typed. */
const PathDialog = ({
  open,
  title,
  action,
  initial,
  problemFor,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  action: string;
  initial: string;
  problemFor: (path: string) => string | null;
  onClose: () => void;
  onSubmit: (path: string) => void;
}) => {
  const [value, setValue] = useState(initial);
  useEffect(() => {
    if (open) setValue(initial);
  }, [open, initial]);
  const path = value.trim();
  const problem = path === initial ? null : problemFor(path);
  const canSubmit = !!path && path !== initial && !problem;

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title={title}
      width="30rem"
      footer={
        <>
          <DsButton size="md" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            size="md"
            variant="action"
            isDisabled={!canSubmit}
            onClick={() => onSubmit(path)}
          >
            {action}
          </DsButton>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) onSubmit(path);
        }}
      >
        <input
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          aria-label="Path"
          placeholder="src/app.js"
          spellCheck={false}
          className="w-full h-10 px-3 rounded-control bg-navy-900 border border-navy-500 focus:border-navy-300 outline-none font-courrier text-ds-135 text-white placeholder:text-navy-300"
        />
        <div
          className={`mt-2 min-h-[1.25rem] text-ds-115 ${
            problem ? "text-state-err" : "text-navy-300"
          }`}
        >
          {problem ||
            "A path from the top of the code. Folders are made as needed."}
        </div>
      </form>
    </ModalDialog>
  );
};

const DiffEditor = lazy(() =>
  import("@monaco-editor/react").then((module) => ({
    default: module.DiffEditor,
  }))
);

/**
 * The code changed under the edits (someone deployed in the meantime), and
 * some files changed on both sides: each is settled here before saving.
 */
const ConflictPanel = ({
  conflicts,
  onKeepMine,
  onUseTheirs,
}: {
  conflicts: Conflict[];
  onKeepMine: (conflict: Conflict) => void;
  onUseTheirs: (conflict: Conflict) => void;
}) => {
  const [index, setIndex] = useState(0);
  const conflict = conflicts[Math.min(index, conflicts.length - 1)];
  if (!conflict) return null;
  return (
    <div className="h-full flex flex-col">
      <div className="flex-none flex flex-wrap items-center gap-3 px-5 py-3 border-b border-navy-500 bg-navy-800">
        <span className="font-courrier text-ds-125 text-white truncate">
          {conflict.path}
        </span>
        <span className="text-ds-115 text-navy-300">
          {conflicts.length === 1
            ? "1 file changed on both sides"
            : `${index + 1} of ${conflicts.length} files changed on both sides`}
        </span>
        <span className="ml-auto flex gap-2">
          <DsButton size="md" onClick={() => onUseTheirs(conflict)}>
            Use theirs
          </DsButton>
          <DsButton
            size="md"
            variant="action"
            onClick={() => onKeepMine(conflict)}
          >
            Keep mine
          </DsButton>
        </span>
      </div>
      <div className="flex-none grid grid-cols-2 px-5 py-1.5 text-ds-105 uppercase tracking-[0.08em] text-navy-300 border-b border-navy-500 bg-navy-800">
        <span>
          {conflict.theirs === null
            ? "Deleted in the code now"
            : "In the code now"}
        </span>
        <span>{conflict.mine === null ? "Deleted here" : "Edited here"}</span>
      </div>
      <div className="flex-1 min-h-0">
        <Suspense fallback={<Notice>Loading editor…</Notice>}>
          <DiffEditor
            height="100%"
            original={conflict.theirs ?? ""}
            modified={conflict.mine ?? ""}
            // Left to the editor: disposing them on unmount, as the wrapper
            // does by default, throws "TextModel got disposed before
            // DiffEditorWidget model got reset" on monaco 0.55, and an error
            // thrown while React unmounts takes the whole screen down.
            keepCurrentOriginalModel
            keepCurrentModifiedModel
            language={languageFor(conflict.path)}
            theme="vs-dark"
            beforeMount={quietDiagnostics}
            options={{
              readOnly: true,
              renderSideBySide: true,
              minimap: { enabled: false },
              fontSize: 13,
              lineHeight: 20,
              fontFamily: EDITOR_FONT,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </Suspense>
      </div>
      {conflicts.length > 1 ? (
        <div className="flex-none flex justify-between px-5 py-2 border-t border-navy-500 bg-navy-800">
          <DsButton
            size="md"
            variant="ghost"
            isDisabled={index === 0}
            onClick={() => setIndex(index - 1)}
          >
            Previous
          </DsButton>
          <DsButton
            size="md"
            variant="ghost"
            isDisabled={index >= conflicts.length - 1}
            onClick={() => setIndex(index + 1)}
          >
            Next
          </DsButton>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Unsaved work, kept outside the component: the app screen re-renders its
 * outlet on a hiccup of the app subscription (an error screen for a beat),
 * and going to another screen and back is a remount too. The work is the
 * one thing here that must not be lost to that. In memory only — a reload
 * of the tab asks first (beforeunload), and after it the code is re-read.
 */
interface Draft {
  workspace: SourceWorkspace;
  base?: string;
  selectedPath: string | null;
}
const DRAFTS = new Map<string, Draft>();

type PathPrompt =
  | { kind: "new"; dir: string }
  | { kind: "rename"; path: string }
  | null;

const CodeViewer = ({
  appId,
  deploymentId,
  reloadKey,
  onPhoneRows,
  onExport,
  isExporting = false,
  onUpload,
  canEdit = false,
  onCodeChanged,
}: {
  appId: string;
  /** Phone: what the viewer offers, as rows of the app header's "⋯". */
  onPhoneRows?: (rows: SheetRow[]) => void;
  /** The whole code as a .zip: the file bar's "Export". */
  onExport?: () => void;
  isExporting?: boolean;
  /** New code, on the desktop's file bar. The phone's head has its own. */
  onUpload?: () => void;
  /** One deployment's code; without it, the app's latest. */
  deploymentId?: string;
  /** The latest upload's id: changes when the app has new code, so the files
   * are listed again, and is what the edits are made on. */
  reloadKey?: string;
  /** The latest code may be edited and saved here (never a deployment's). */
  canEdit?: boolean;
  /** The latest code is another upload now (the edits were saved and are
   * deploying, or the code was updated under them): re-read it. */
  onCodeChanged?: () => void;
}) => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState<
    Record<string, Loadable<SourceFolder>>
  >({});
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState<Loadable<SourceSearch> | null>(null);
  // Phone: the tree and the versions are sheets, open only while choosing.
  const [phoneSheet, setPhoneSheet] = useState<"files" | "file" | null>(null);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<SourceFile | null>(null);
  const [content, setContent_] = useState<SourceFileContent | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  // Bumped when the code changes, so an answer about the old code is dropped.
  const generation = useRef(0);

  // The editor's unsaved work. Survives a reload of the tree (new code).
  const editing = canEdit && !deploymentId;
  const draft = editing ? DRAFTS.get(appId) : undefined;
  const [workspace, setWorkspace] = useState<SourceWorkspace>(
    draft ? draft.workspace : EMPTY_WORKSPACE
  );
  const dirty = useMemo(() => dirtyPaths(workspace), [workspace]);
  const [prompt, setPrompt] = useState<PathPrompt>(null);
  const [isDiscardAllOpen, setIsDiscardAllOpen] = useState(false);
  const [saving, setSaving] = useState<"idle" | "saving" | "deploying">("idle");
  // The upload the edits were made on. It stays while there is unsaved work,
  // whatever the latest becomes: the API checks the save against it, and a
  // newer latest under the work means the code changed meanwhile — someone
  // deployed — which is told (isStale) and settled by `update`.
  const [base, setBase] = useState(draft ? draft.base : reloadKey);
  const [isStale, setIsStale] = useState(false);
  useEffect(() => {
    if (reloadKey === base) return;
    if (isDirty(workspace)) {
      setIsStale(true);
    } else {
      setBase(reloadKey);
    }
    // Only when the latest moves: the work's own changes do not move the base.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);
  const [isRebasing, setIsRebasing] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  // A link clicked while there is unsaved work: asked about before followed.
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  // Upload asked for while there is unsaved work: it replaces the code.
  const [isUploadPending, setIsUploadPending] = useState(false);
  // Kept across a reload of the tree, so saving does not lose the place.
  const selectedPathRef = useRef<string | null>(
    draft ? draft.selectedPath : null
  );

  useEffect(() => {
    if (!editing) return;
    if (isDirty(workspace)) {
      DRAFTS.set(appId, {
        workspace,
        base,
        selectedPath: selectedPathRef.current,
      });
    } else {
      DRAFTS.delete(appId);
    }
  }, [editing, appId, workspace, base]);

  const select = (file: SourceFile) => {
    setSelected(file);
    selectedPathRef.current = file.path;
    const kept = DRAFTS.get(appId);
    if (kept) kept.selectedPath = file.path;
    setPhoneSheet(null);
  };

  const loadFolder = useCallback(
    (dir: string) => {
      const asked = generation.current;
      setFolders((prev) => ({ ...prev, [dir]: { status: "loading" } }));
      return listSourceFolder(appId, dir, deploymentId)
        .then((folder) => {
          if (asked !== generation.current) return null;
          setFolders((prev) => ({
            ...prev,
            [dir]: { status: "ready", ...folder },
          }));
          return folder;
        })
        .catch((e) => {
          if (asked !== generation.current) return null;
          setFolders((prev) => ({
            ...prev,
            [dir]: {
              status: "error",
              message: errorText(e, "This folder could not be loaded."),
            },
          }));
          return null;
        });
    },
    [appId, deploymentId]
  );

  // The top folder, and the file the viewer opens on: the one open before
  // the reload when there was one, else the file that says what the app is.
  useEffect(() => {
    generation.current += 1;
    setFolders({});
    setOpenFolders(new Set());
    setSelected(null);
    setContent_(null);
    setQuery("");
    setSearch(null);
    loadFolder("").then((root) => {
      if (!root) return;
      const files = root.entries.filter((entry) => entry.type === "FILE");
      const kept = selectedPathRef.current;
      if (kept) {
        const dir = dirOf(kept);
        if (dir) {
          // Open the folders down to it; its own listing says whether it is there.
          const parts = dir.split("/");
          setOpenFolders(
            new Set(parts.map((_, i) => parts.slice(0, i + 1).join("/")))
          );
          parts.forEach((_, i) => loadFolder(parts.slice(0, i + 1).join("/")));
        }
        setSelected({ path: kept, size: 0 });
        return;
      }
      const first =
        INITIAL_FILES.map((name) =>
          files.find((entry) => entry.name === name)
        ).find(Boolean) || files[0];
      if (first && first.type === "FILE") {
        setSelected({ path: first.path, size: first.size });
        selectedPathRef.current = first.path;
      }
    });
  }, [loadFolder, reloadKey]);

  // Search runs on the API once typing pauses.
  const trimmed = query.trim();
  useEffect(() => {
    if (!trimmed) {
      setSearch(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearch({ status: "loading" });
      searchSourceFiles(appId, trimmed, deploymentId)
        .then(
          (result) => !cancelled && setSearch({ status: "ready", ...result })
        )
        .catch(
          (e) =>
            !cancelled &&
            setSearch({
              status: "error",
              message: errorText(e, "The search did not go through."),
            })
        );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [appId, deploymentId, trimmed, reloadKey]);

  // The selected file's bytes come from the path it was uploaded under: a
  // renamed file reads from where it was; a file made here has none to read.
  const sourcePath = selected ? sourcePathOf(workspace, selected.path) : null;
  useEffect(() => {
    if (!selected) return;
    setContent_(null);
    setContentError(null);
    if (sourcePath === null) return;
    let cancelled = false;
    sourceFileContent(appId, sourcePath, deploymentId)
      .then((result) => {
        if (cancelled) return;
        setContent_(result);
        if (result.kind === "TEXT") {
          setWorkspace((ws) => withOriginal(ws, selected.path, result.content));
        }
      })
      .catch(
        (e) =>
          !cancelled &&
          setContentError(errorText(e, "This file could not be opened."))
      );
    return () => {
      cancelled = true;
    };
  }, [appId, selected, sourcePath, deploymentId]);

  // Opening a folder asks for it the first time, and again after a failure.
  // A folder that only exists through unsaved files has nothing to ask for.
  const toggle = (entry: OverlaidEntry) => {
    const path = entry.path;
    const isOpen = openFolders.has(path);
    setOpenFolders((prev) => {
      const next = new Set(prev);
      isOpen ? next.delete(path) : next.add(path);
      return next;
    });
    const folder = folders[path];
    if (isOpen || (folder && folder.status !== "error")) return;
    if (entry.type === "FOLDER" && entry.isVirtual) return;
    loadFolder(path);
  };

  // A file made here: no bytes to read, so it is text from the start.
  const presentFile = selected ? workspace.present[selected.path] : undefined;
  const isNew = !!presentFile && presentFile.from === null;
  const isDeleted =
    !!selected && workspace.removed[selected.path] === "deleted";
  const text =
    presentFile && presentFile.content !== null
      ? presentFile.content
      : content && content.kind === "TEXT"
      ? content.content
      : isNew
      ? ""
      : null;
  const original =
    presentFile && presentFile.original !== null
      ? presentFile.original
      : content && content.kind === "TEXT"
      ? content.content
      : null;
  const selectedStatus = selected ? statusOf(workspace, selected.path) : null;

  const onEdit = (value: string | undefined) => {
    if (!selected || !editing || value === undefined) return;
    setWorkspace((ws) => setContent(ws, selected.path, value, original));
  };

  // The copy button turns into a check for a moment: the feedback is on the
  // button, as in the Agents code panel, not a toast.
  const [isCopied, setIsCopied] = useState(false);
  useEffect(() => {
    if (!isCopied) return;
    const timer = window.setTimeout(() => setIsCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [isCopied]);
  useEffect(() => setIsCopied(false), [selected]);

  const copy = async () => {
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
    } catch (e) {
      toast.error("Could not copy to the clipboard.", {
        className: TOAST_SURFACE,
      });
    }
  };

  const downloadFile = () => {
    if (text === null || !selected) return;
    saveText(selected.path, text);
  };

  // Whether an uploaded file is known at a path: what the tree has listed.
  // The API is the judge of the rest, and says so on save.
  const knownPath = (path: string) =>
    Object.values(folders).some(
      (folder) =>
        folder.status === "ready" &&
        folder.entries.some(
          (entry) => entry.type === "FILE" && entry.path === path
        )
    );

  const actions: TreeActions | undefined = editing
    ? {
        newFile: (dir) => setPrompt({ kind: "new", dir }),
        rename: (path) => setPrompt({ kind: "rename", path }),
        remove: (path) => {
          setWorkspace((ws) => deleteFile(ws, path));
        },
        discard: (path) => setWorkspace((ws) => discardFile(ws, path)),
      }
    : undefined;

  const submitPath = (path: string) => {
    if (!prompt) return;
    if (prompt.kind === "new") {
      setWorkspace((ws) => createFile(ws, path));
      const dir = dirOf(path);
      if (dir) {
        setOpenFolders((prev) => {
          const next = new Set(prev);
          const parts = dir.split("/");
          parts.forEach((_, i) => next.add(parts.slice(0, i + 1).join("/")));
          return next;
        });
      }
      select({ path, size: 0 });
    } else {
      const from = prompt.path;
      setWorkspace((ws) => renameFile(ws, from, path));
      if (selected && selected.path === from)
        select({ path, size: selected.size });
    }
    setPrompt(null);
  };

  const discardAll = () => {
    setWorkspace(EMPTY_WORKSPACE);
    setConflicts([]);
    setIsStale(false);
    setBase(reloadKey);
    setIsDiscardAllOpen(false);
  };

  // Save & deploy: the work becomes one upload made from the latest code,
  // and that upload deploys, the way an uploaded folder does. A refused
  // deploy (the free tier's one at a time) leaves the work here to try again.
  const save = async () => {
    if (!base || saving !== "idle") return;
    setSaving("saving");
    try {
      const edited = await editSource(appId, editsBody(workspace, base));
      setSaving("deploying");
      await deployUpload(appId, edited.uploadId, edited.manifestSha256);
      const changed =
        edited.changed + edited.created + edited.deleted + edited.renamed;
      toast.success(
        `Deploying edited code · ${changed} ${
          changed === 1 ? "file" : "files"
        } changed`,
        { className: TOAST_SURFACE }
      );
      setWorkspace(EMPTY_WORKSPACE);
      setConflicts([]);
      setIsStale(false);
      setBase(reloadKey);
      onCodeChanged?.();
    } catch (e) {
      if (e instanceof ApiV2Error && e.code === "SOURCE_CHANGED") {
        setIsStale(true);
      } else {
        toast.error(userFacingError(e, "The edits could not be saved."), {
          className: TOAST_SURFACE,
        });
      }
    } finally {
      setSaving("idle");
    }
  };

  // The work carried onto the code as it is now. Files the newer code left
  // alone keep their edits; the rest come back as conflicts to settle.
  const update = async () => {
    setIsRebasing(true);
    try {
      const read = async (path: string) => {
        try {
          const result = await sourceFileContent(appId, path);
          return result.kind === "TEXT" ? result.content : null;
        } catch (e) {
          if (e instanceof ApiV2Error && e.status === 404) return null;
          throw e;
        }
      };
      const latest = await getAppSource(appId);
      const result = await rebase(workspace, read);
      setWorkspace(result.workspace);
      setConflicts(result.conflicts);
      setIsStale(false);
      // The work is on the latest code now: that is the base, and the tree
      // reloads on it (the parent re-reads the latest) with the work kept.
      setBase(latest.latest ? latest.latest.uploadId : reloadKey);
      onCodeChanged?.();
    } catch (e) {
      toast.error(userFacingError(e, "The latest code could not be read."), {
        className: TOAST_SURFACE,
      });
    } finally {
      setIsRebasing(false);
    }
  };

  // Told early, not only on save: with unsaved work, coming back to the tab
  // asks whether the code is still the one the edits were made on.
  useEffect(() => {
    if (!editing || dirty.length === 0 || !base) return;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      getAppSource(appId)
        .then((source) => {
          if (source.latest && source.latest.uploadId !== base)
            setIsStale(true);
        })
        .catch(() => undefined);
    };
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [editing, dirty.length, base, appId]);

  // The guards. Closing or reloading the tab: the browser asks. A link in
  // the app: caught before the router sees it (capture, on the document —
  // the sidebar, the top bar, all of them), and asked about here. Modified
  // clicks open a new tab, where there is nothing to lose.
  useEffect(() => {
    if (dirty.length === 0) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const target = event.target as Element | null;
      const anchor =
        target && target.closest ? target.closest("a[href]") : null;
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const url = new URL(
        anchor.getAttribute("href") || "",
        window.location.href
      );
      if (url.origin !== window.location.origin) return;
      const to = url.pathname + url.search + url.hash;
      const here =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (to === here) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingHref(to);
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty.length]);

  const canSave = dirty.length > 0 && conflicts.length === 0 && !isStale;
  const saveLabel =
    saving === "saving"
      ? "Saving…"
      : saving === "deploying"
      ? "Deploying…"
      : "Save & deploy";

  // What the file bar holds on the right: the open file's copy and download,
  // then the export of the whole code and the upload of new code. Both stay
  // while there is unsaved work — an upload replaces the code, so it asks
  // first; the work itself is saved or dropped from the bar under the editor.
  // The open file's edits: behind the bar's "⋯" on the desktop, and in the
  // phone's one menu together with everything else.
  const editItems =
    actions && selected && conflicts.length === 0
      ? [
          ...(selectedStatus
            ? [
                {
                  label:
                    selectedStatus === "deleted"
                      ? "Restore"
                      : "Discard changes",
                  onClick: () => actions.discard(selected.path),
                },
              ]
            : []),
          ...(!isDeleted
            ? [
                {
                  label: "Rename",
                  onClick: () => actions.rename(selected.path),
                },
                {
                  label: "Delete",
                  onClick: () => actions.remove(selected.path),
                  isDanger: true,
                },
              ]
            : []),
        ]
      : [];

  // Phone: the viewer's actions are rows of the app header's "⋯", handed up
  // whenever what they can do changes. The handlers are read from a ref so
  // the rows do not have to be rebuilt on every keystroke.
  const handlers = useRef({ copy, downloadFile, onExport, actions });
  handlers.current = { copy, downloadFile, onExport, actions };
  const selectedPath = selected ? selected.path : null;
  const hasText = text !== null;
  const hasExport = !!onExport;
  const canEditSelected = !!actions && !!selected && conflicts.length === 0;
  useEffect(() => {
    if (!onPhoneRows) return;
    const h = handlers.current;
    // Few rows, the ones used from a phone: the open file's edits are in
    // the Files sheet, beside the file.
    const rows: SheetRow[] = [
      ...(hasText && selectedPath && !isDeleted
        ? [{ label: "Copy file", onClick: () => h.copy() }]
        : []),
      ...(hasExport
        ? [
            {
              label: isExporting ? "Preparing the .zip…" : "Download .zip",
              isDisabled: isExporting,
              onClick: () => h.onExport?.(),
            },
          ]
        : []),
    ];
    onPhoneRows(rows);
  }, [onPhoneRows, hasText, selectedPath, isDeleted, hasExport, isExporting]);

  const root = folders[""];
  if (root && root.status === "error") {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 text-ds-125 text-state-err"
      >
        <AlertIcon size={14} /> {root.message}
      </div>
    );
  }

  const fileActions = (
    <span className="flex flex-none items-center gap-1.5">
      {text !== null && selected && !isDeleted ? (
        <>
          <BarIconButton
            label={isCopied ? "Copied" : "Copy file contents"}
            onClick={copy}
          >
            {isCopied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          </BarIconButton>
          <BarIconButton label="Download this file" onClick={downloadFile}>
            <DownloadIcon size={14} />
          </BarIconButton>
        </>
      ) : null}
      {editItems.length ? (
        <BarMenu label="More actions for this file" items={editItems} />
      ) : null}
      {onExport ? (
        <ExportButton onClick={onExport} isBusy={isExporting} />
      ) : null}
      {onUpload ? (
        <UploadButton
          onClick={() =>
            dirty.length > 0 ? setIsUploadPending(true) : onUpload()
          }
        />
      ) : null}
    </span>
  );

  // Under the editor while there is unsaved work, as the Agents code panel
  // has it: what the work is, and the two things to do with it.
  // Under the editor on the desktop, as the Agents code panel has it. On the
  // phone the page scrolls and the editor is tall, so a bar under it was
  // below the fold: there it sits above the code, under the pickers.
  const saveBar = (placement: "desktop" | "phone") =>
    editing && dirty.length > 0 ? (
      <div
        className={
          placement === "desktop"
            ? "flex-none flex flex-wrap items-center gap-3 px-5 py-3 bg-navy-800 border-t border-navy-500 [@media(max-width:767.98px)]:hidden"
            : "hidden [@media(max-width:767.98px)]:flex flex-none flex-wrap items-center gap-3 px-3.5 py-2.5 rounded-card border border-navy-400 bg-navy-800"
        }
      >
        <span className="text-ds-125 text-navy-100">
          {dirty.length === 1
            ? "1 file changed."
            : `${dirty.length} files changed.`}{" "}
          {placement === "phone"
            ? null
            : conflicts.length > 0
            ? "Settle the conflicts above to save."
            : isStale
            ? "Update to the latest code to save."
            : "Save sends them as new code and deploys it."}
        </span>
        <span className="ml-auto flex gap-2">
          <DsButton
            size="md"
            variant="danger"
            isDisabled={saving !== "idle"}
            onClick={() => setIsDiscardAllOpen(true)}
          >
            Discard
          </DsButton>
          <DsButton
            size="md"
            variant="action"
            isDisabled={!canSave || saving !== "idle"}
            onClick={save}
          >
            {saveLabel}
          </DsButton>
        </span>
      </div>
    ) : null;

  const staleBar =
    editing && (isStale || conflicts.length > 0) ? (
      <div className="flex-none flex flex-wrap items-center gap-3 px-5 py-2.5 bg-state-warn/10 border-b border-state-warn/40 text-ds-125 text-white">
        <AlertIcon size={14} className="flex-none text-state-warn" />
        {conflicts.length > 0 ? (
          <span>
            {conflicts.length === 1
              ? "One file changed in the code and here. Settle it to save."
              : `${conflicts.length} files changed in the code and here. Settle them to save.`}
          </span>
        ) : (
          <span>The code changed since you opened it.</span>
        )}
        {isStale ? (
          <DsButton
            size="md"
            variant="outline"
            className="ml-auto"
            isDisabled={isRebasing}
            onClick={update}
          >
            {isRebasing ? "Updating…" : "Update keeping my changes"}
          </DsButton>
        ) : null}
      </div>
    ) : null;

  const tree =
    search && trimmed ? (
      <SearchRows
        search={search}
        workspace={workspace}
        selected={selected?.path || null}
        onSelect={select}
      />
    ) : (
      <FolderRows
        dir=""
        depth={0}
        folders={folders}
        workspace={workspace}
        open={openFolders}
        selected={selected?.path || null}
        onToggle={toggle}
        onSelect={select}
        actions={actions}
      />
    );

  // The phone's stale bar sits above the code box, not inside it.
  const staleBarPhone = staleBar ? (
    <div className="hidden [@media(max-width:767.98px)]:block rounded-card overflow-hidden border border-state-warn/40">
      {staleBar}
    </div>
  ) : null;

  // Phone: the footer is fixed above the tab bar while there is unsaved
  // work — the page scrolls, and a bar under the editor was below the fold.
  const phoneFooter =
    editing && dirty.length > 0 ? (
      <div className="hidden [@media(max-width:767.98px)]:flex fixed z-20 left-0 right-0 bottom-[calc(61px_+_env(safe-area-inset-bottom))] short:bottom-[calc(41px_+_env(safe-area-inset-bottom))] items-center gap-2 px-4 py-3 bg-navy-800 border-t border-navy-500">
        <span className="flex-1 min-w-0 truncate text-ds-125 text-navy-200">
          {conflicts.length > 0
            ? "Settle the conflicts"
            : isStale
            ? "Update to save"
            : "Unsaved changes"}
        </span>
        <DsButton
          size="lg"
          variant="outline"
          isDisabled={saving !== "idle"}
          onClick={() => setIsDiscardAllOpen(true)}
        >
          Discard
        </DsButton>
        <DsButton
          size="lg"
          variant="action"
          isDisabled={!canSave || saving !== "idle"}
          onClick={save}
        >
          {saveLabel}
        </DsButton>
      </div>
    ) : null;

  return (
    <div
      className={`h-full flex flex-col gap-3 ${
        editing && dirty.length > 0 ? "[@media(max-width:767.98px)]:pb-16" : ""
      }`}
    >
      {/* Phone: the file picker above the code, opening a sheet from the
          bottom, and the open file's actions beside it. */}
      <div className="hidden [@media(max-width:767.98px)]:flex flex-none items-stretch gap-2.5">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={phoneSheet === "files"}
          onClick={() => setPhoneSheet("files")}
          className="b4-focus flex-1 min-w-0 h-11 flex items-center gap-2 rounded-card border border-navy-400 bg-navy-800 px-3.5 font-courrier text-ds-135 text-white"
        >
          <span className="truncate">{selected?.path || "Pick a file"}</span>
          <span className="ml-auto flex items-center gap-2">
            <StatusMark status={selectedStatus} />
            <span className="text-[10px] text-navy-300" aria-hidden>
              ▾
            </span>
          </span>
        </button>
        {/* The open file's edits, one tap from its name: a sheet of three
            rows. Beside the pickers rather than inside the Files sheet, which
            is for choosing a file, not doing things to one. */}
        {canEditSelected ? (
          <button
            type="button"
            aria-label="Actions for this file"
            aria-haspopup="dialog"
            aria-expanded={phoneSheet === "file"}
            onClick={() => setPhoneSheet("file")}
            className="b4-focus flex-none w-11 h-11 flex items-center justify-center rounded-card border border-navy-400 bg-navy-800 text-navy-100"
          >
            <MoreHorizontalIcon size={16} />
          </button>
        ) : null}
      </div>
      {staleBarPhone}

      {/* Desktop: the tree beside the editor. Phone: the editor alone, and the
        tree over it while picking a file. No frame around the pair: it is the
        whole screen, and a box around it was one more edge between the page
        and the code. The rule on top is what separates it from the head. */}
      <div className="relative flex flex-1 min-h-0 border-t border-navy-500 overflow-hidden [@media(max-width:767.98px)]:rounded-card [@media(max-width:767.98px)]:border [@media(max-width:767.98px)]:border-navy-500">
        <div className="w-72 flex-none flex flex-col bg-navy-800 border-r border-navy-500 [@media(max-width:767.98px)]:hidden">
          {/* The same height and rule as the editor's file bar beside it, so
            the two read as one line across the box. */}
          <div className="flex-none h-14 px-2.5 flex items-center gap-1.5 border-b border-navy-500">
            <div className="flex-1 min-w-0 flex items-center gap-2 h-9 px-2.5 rounded-control bg-navy-900 border border-navy-500 focus-within:border-navy-300">
              <SearchIcon className="flex-none text-navy-300" />
              <input
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-inter text-ds-135 text-white placeholder:text-navy-300"
                placeholder="Search files"
                aria-label="Search files"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            {actions ? (
              <button
                type="button"
                aria-label="New file"
                title="New file"
                onClick={() => actions.newFile("")}
                className="b4-focus flex-none w-9 h-9 flex items-center justify-center rounded-control border border-navy-500 bg-navy-900 text-navy-200 hover:text-white hover:border-navy-300"
              >
                <PlusIcon size={15} />
              </button>
            ) : null}
          </div>
          <div className="flex-1 min-h-0 overflow-auto px-2.5 pt-3 pb-2.5">
            {tree}
          </div>
        </div>

        <div
          className="flex-1 min-w-0 flex flex-col"
          style={{ background: EDITOR_BACKGROUND }}
        >
          <div className="flex-none flex items-center justify-between gap-3 h-14 px-5 bg-navy-800 border-b border-navy-500 [@media(max-width:767.98px)]:hidden">
            <span className="min-w-0 flex items-center gap-2 font-courrier text-ds-125 text-white">
              <span className="truncate">{selected?.path || ""}</span>
              <StatusMark status={selectedStatus} />
            </span>
            {fileActions}
          </div>
          <span className="[@media(max-width:767.98px)]:hidden">
            {staleBar}
          </span>
          <div className="flex-1 min-h-0">
            {conflicts.length > 0 ? (
              <ConflictPanel
                conflicts={conflicts}
                onKeepMine={(conflict) => {
                  setWorkspace((ws) => keepMine(ws, conflict));
                  setConflicts((list) => list.filter((c) => c !== conflict));
                }}
                onUseTheirs={(conflict) => {
                  setWorkspace((ws) => takeTheirs(ws, conflict));
                  setConflicts((list) => list.filter((c) => c !== conflict));
                }}
              />
            ) : contentError ? (
              <Notice>{contentError}</Notice>
            ) : !selected ? (
              <Notice>Pick a file on the left.</Notice>
            ) : isDeleted ? (
              <Notice>
                <span>This file is deleted in the next save.</span>
                <DsButton
                  size="md"
                  onClick={() => actions?.discard(selected.path)}
                >
                  Restore
                </DsButton>
              </Notice>
            ) : text === null && !content ? (
              <Notice>Loading…</Notice>
            ) : text === null && content && content.kind !== "TEXT" ? (
              <Notice>
                {content.kind === "BINARY"
                  ? "This is a binary file. Use Export to get it."
                  : "This file is larger than 1 MB, too large to show here. Use Export to get it."}
              </Notice>
            ) : (
              <Suspense fallback={<Notice>Loading editor…</Notice>}>
                <Editor
                  height="100%"
                  path={`${reloadKey || "code"}/${selected.path}`}
                  language={languageFor(selected.path)}
                  // Read-only: without the file's final newline, so the last
                  // numbered line is the last line of text ("5 lines" shows
                  // 5). Editing keeps the file exactly as it is.
                  value={
                    editing
                      ? (text as string)
                      : (text as string).replace(/\r?\n$/, "")
                  }
                  onChange={onEdit}
                  theme="vs-dark"
                  beforeMount={quietDiagnostics}
                  options={{
                    readOnly: !editing,
                    domReadOnly: !editing,
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineHeight: 20,
                    fontFamily: EDITOR_FONT,
                    lineNumbers: "on",
                    lineNumbersMinChars: 4,
                    glyphMargin: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    padding: { top: 14, bottom: 14 },
                    renderLineHighlight: editing ? "line" : "none",
                    scrollbar: {
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      useShadows: false,
                    },
                    overviewRulerBorder: false,
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                    quickSuggestions: editing,
                    contextmenu: editing,
                    folding: true,
                    showFoldingControls: "mouseover",
                  }}
                />
              </Suspense>
            )}
          </div>
          {saveBar("desktop")}
        </div>
      </div>

      <BottomSheet
        open={phoneSheet === "files"}
        onClose={() => setPhoneSheet(null)}
        title="Files"
      >
        <div className="px-2.5 pt-2.5 pb-1 flex items-center gap-1.5">
          <div className="flex-1 min-w-0 flex items-center gap-2 h-10 px-2.5 rounded-control bg-navy-900 border border-navy-500 focus-within:border-navy-300">
            <SearchIcon className="flex-none text-navy-300" />
            <input
              className="flex-1 min-w-0 bg-transparent border-0 outline-none font-inter text-ds-135 text-white placeholder:text-navy-300"
              placeholder="Search files"
              aria-label="Search files"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {actions ? (
            <button
              type="button"
              aria-label="New file"
              title="New file"
              onClick={() => {
                setPhoneSheet(null);
                actions.newFile("");
              }}
              className="b4-focus flex-none w-10 h-10 flex items-center justify-center rounded-control border border-navy-500 bg-navy-900 text-navy-200 hover:text-white"
            >
              <PlusIcon size={15} />
            </button>
          ) : null}
        </div>
        <div className="px-2.5 pb-2.5">{tree}</div>
      </BottomSheet>

      {canEditSelected && selectedPath ? (
        <BottomSheet
          open={phoneSheet === "file"}
          onClose={() => setPhoneSheet(null)}
          title={selectedPath}
        >
          <SheetRows onPick={() => setPhoneSheet(null)} rows={editItems} />
        </BottomSheet>
      ) : null}

      {phoneFooter}

      <PathDialog
        open={prompt !== null}
        title={prompt?.kind === "rename" ? "Rename file" : "New file"}
        action={prompt?.kind === "rename" ? "Rename" : "Create"}
        initial={
          prompt?.kind === "rename"
            ? prompt.path
            : prompt?.kind === "new" && prompt.dir
            ? `${prompt.dir}/`
            : ""
        }
        problemFor={(path) => pathProblem(workspace, path, knownPath)}
        onClose={() => setPrompt(null)}
        onSubmit={submitPath}
      />

      <ModalDialog
        open={isDiscardAllOpen}
        onClose={() => setIsDiscardAllOpen(false)}
        title="Discard changes?"
        blurb={`${dirty.length} ${
          dirty.length === 1 ? "file has" : "files have"
        } unsaved changes. Discarding puts the code back the way it was uploaded.`}
        footer={
          <>
            <DsButton size="md" onClick={() => setIsDiscardAllOpen(false)}>
              Keep editing
            </DsButton>
            <DsButton size="md" variant="danger" onClick={discardAll}>
              Discard
            </DsButton>
          </>
        }
      />

      <ModalDialog
        open={pendingHref !== null}
        onClose={() => setPendingHref(null)}
        title="Unsaved changes"
        blurb={`${dirty.length} ${
          dirty.length === 1 ? "file has" : "files have"
        } unsaved changes. Leaving discards ${
          dirty.length === 1 ? "it" : "them"
        }.`}
        footer={
          <>
            <DsButton size="md" onClick={() => setPendingHref(null)}>
              Keep editing
            </DsButton>
            <DsButton
              size="md"
              variant="danger"
              onClick={() => {
                const href = pendingHref;
                setPendingHref(null);
                setWorkspace(EMPTY_WORKSPACE);
                setBase(reloadKey);
                if (href) navigate(href);
              }}
            >
              Discard and leave
            </DsButton>
          </>
        }
      />
      <ModalDialog
        open={isUploadPending}
        onClose={() => setIsUploadPending(false)}
        title="Upload new code?"
        blurb={`${dirty.length} ${
          dirty.length === 1 ? "file has" : "files have"
        } unsaved changes here. The uploaded folder replaces the code, and they are lost.`}
        footer={
          <>
            <DsButton size="md" onClick={() => setIsUploadPending(false)}>
              Keep editing
            </DsButton>
            <DsButton
              size="md"
              variant="danger"
              onClick={() => {
                setIsUploadPending(false);
                setWorkspace(EMPTY_WORKSPACE);
                setConflicts([]);
                setIsStale(false);
                setBase(reloadKey);
                onUpload?.();
              }}
            >
              Discard and upload
            </DsButton>
          </>
        }
      />
    </div>
  );
};

export default CodeViewer;
